import React, { useEffect, useRef } from 'react';

import InfoIcon from 'icons/info.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import CollectionSelect from 'components/application/CollectionSelect';
import ErrorMessage from 'components/application/ErrorMessage';
import RadioField from 'components/application/RadioField';
import TextField from 'components/application/TextField';
import Tooltip from 'components/application/Tooltip';

import AddButton from 'components/buttons/AddButton';
import FieldOptions from 'components/settings/personnel/information/FieldOptions';

import { focusField } from 'components/helpers/refs';

const fieldTypeValues = [
  'singleLineText',
  'multiLineText',
  'select',
  'date',
  'email',
];

export default function FieldForm({
  fieldType,
  name,
  onFormOptionAppend,
  onFormOptionDelete,
  onFormOptionOrderChange,
  onFormOptionValueChange,
  onFormValueChange,
  options,
  removeErrorStyling,
  requestError,
  restricted,
  sidePanel,
}) {
  const nameInputRef = useRef();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.personnel.information.FieldForm',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const onNameChange = ({ target }) => {
    onFormValueChange({ key: 'name', value: target.value });
  };

  const removeNameErrorStyling = () => {
    removeErrorStyling({ key: 'name' });
  };

  const onFieldTypeChange = ({ value }) => {
    onFormValueChange({ key: 'fieldType', value });
  };

  const onVisibilityChange = ({ target }) => {
    onFormValueChange({
      key: 'restricted',
      value: target.value === 'restricted',
    });
  };

  const removeFieldOptionsErrorStyling = () => {
    removeErrorStyling({ key: 'fieldOptions' });
  };

  const fieldTypeOptions = fieldTypeValues.map((value) => ({
    label: i18nTCommon(`personnel.fieldAttributeTypes.${value}`),
    value,
  }));

  useEffect(() => {
    if (sidePanel.isOpen) focusField(nameInputRef);
  }, [sidePanel.isOpen]);

  return (
    <div className='popup__body-form popup__body-form--no-side-padding'>
      <ErrorMessage
        isFallback={requestError.isFallback}
        modifiers='tw-mx-5'
        validationErrors={requestError.validationErrors}
        wrapperClassName='form-container'
      />
      <div className='form-container tw-mb-0'>
        {sidePanel.context === 'edit' && (
          <div className='tw-m-0 tw-mx-5 tw-mb-5 tw-flex tw-rounded-lg tw-border-0 tw-bg-cyan-025 tw-p-3 tw-text-cyan-800'>
            <div className='tw-mr-3 tw-h-5'>
              <InfoIcon
                className='[&_path]:tw-fill-cyan-800'
                height={20}
                width={20}
              />
            </div>
            <p className='tw-m-0'>{i18nT('information')}</p>
          </div>
        )}
        <div className='form-container-section'>
          <TextField
            fieldError={requestError.validationErrors.name}
            inputRef={nameInputRef}
            isRequired={true}
            label={i18nTCommon('shared.name')}
            name='name'
            onChange={onNameChange}
            removeErrorStyling={removeNameErrorStyling}
            value={name}
          />
          <div className='tooltip-parent'>
            <CollectionSelect
              isDisabled={sidePanel.context === 'edit'}
              isRequired={true}
              isSearchable={true}
              label={i18nTCommon('personnel.fieldType')}
              name='fieldType'
              onChange={
                sidePanel.context === 'edit' ?
                  () => undefined
                : onFieldTypeChange
              }
              options={fieldTypeOptions}
              placeholder={i18nT('fieldType.placeholder')}
              value={
                fieldType ?
                  fieldTypeOptions.find((option) => option.value === fieldType)
                : null
              }
            />
            {sidePanel.context === 'edit' && (
              <Tooltip
                className='tooltip-dark--max-w-xxs tw-top-3.5'
                placement='left'
                tooltip={i18nT('fieldType.disabledTooltip')}
                trigger='hover'
              />
            )}
          </div>
          <label className='field__label tw-font-medium' htmlFor='visibility'>
            {i18nTCommon('shared.visibility')}
          </label>
          <RadioField
            checked={restricted}
            label={i18nTCommon('shared.restricted')}
            name='visibility'
            onChange={onVisibilityChange}
            value='restricted'
          />
          <div className='-tw-mt-4 tw-ml-7 tw-text-s tw-font-normal tw-tracking-wide'>
            {i18nT('visibility.restricted.description')}
          </div>
          <RadioField
            checked={!restricted}
            label={i18nTCommon('shared.visible')}
            name='visibility'
            onChange={onVisibilityChange}
            value='visible'
          />
          <div className='-tw-mt-4 tw-ml-7 tw-text-s tw-font-normal tw-tracking-wide'>
            {i18nT('visibility.visible.description')}
          </div>
        </div>
        {fieldType === 'select' && (
          <>
            <hr className='tw-my-8 tw-h-px tw-border-0 tw-bg-grey-100' />
            <div className='tw-mx-5 tw-mb-5'>
              <label className='field__label tw-mb-0 tw-font-medium'>
                {i18nT('dropdownList.label')}
              </label>
            </div>
            {sidePanel.context === 'edit' && (
              <div className='tw-m-0 tw-mx-5 tw-mb-5 tw-flex tw-rounded-lg tw-border-0 tw-bg-cyan-025 tw-p-3 tw-text-cyan-800'>
                <div className='tw-mr-3 tw-h-5'>
                  <InfoIcon
                    className='[&_path]:tw-fill-cyan-800'
                    height={20}
                    width={20}
                  />
                </div>
                <p className='tw-m-0'>{i18nT('dropdownList.description')}</p>
              </div>
            )}
            <FieldOptions
              fieldError={requestError.validationErrors.fieldOptions}
              onOptionDelete={onFormOptionDelete}
              onOptionOrderChange={onFormOptionOrderChange}
              onOptionValueChange={onFormOptionValueChange}
              options={options}
              removeErrorStyling={removeFieldOptionsErrorStyling}
            />
            <div className='tw-mx-5 tw-mt-5'>
              <AddButton
                onClick={onFormOptionAppend}
                text={i18nTCommon('shared.addAnother')}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

FieldForm.propTypes = {
  name: PropTypes.string.isRequired,
  fieldType: PropTypes.string.isRequired,
  restricted: PropTypes.bool.isRequired,
  options: PropTypes.object.isRequired,
  requestError: PropTypes.exact({
    isFallback: PropTypes.bool.isRequired,
    validationErrors: PropTypes.object.isRequired,
  }).isRequired,
  sidePanel: PropTypes.exact({
    context: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
  }).isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
  onFormValueChange: PropTypes.func.isRequired,
  onFormOptionValueChange: PropTypes.func.isRequired,
  onFormOptionAppend: PropTypes.func.isRequired,
  onFormOptionDelete: PropTypes.func.isRequired,
  onFormOptionOrderChange: PropTypes.func.isRequired,
};
